import type { ReactNode } from 'react';
import { toast } from 'sonner';

export interface HeaderAction {
  key: string;
  label: string;
  href?: string;
  onClick?: () => void;
  external?: boolean;
  to?: string;             // react-router link
  iconPath?: ReactNode;    // optional inline svg path content
}

interface Props {
  badge: ReactNode;
  badgeClassName?: string;
  category?: string | null;
  value: ReactNode;
  copyValue?: string;
  actions?: HeaderAction[];
  belowValue?: ReactNode;   // aliases / sub-of / etc.
  metaStrip?: ReactNode;    // CVE badges, country, …
}

const COPY_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
);

const EXT_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
);

export default function EntityHeader({ badge, badgeClassName, category, value, copyValue, actions, belowValue, metaStrip }: Props) {
  const handleCopy = () => {
    if (copyValue == null) return;
    void navigator.clipboard.writeText(copyValue);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="ioc-detail-header">
      <div className="ioc-detail-header-top">
        <span className={`ioc-detail-type-badge ${badgeClassName || ''}`}>{badge}</span>
        <div className="ioc-detail-actions">
          {copyValue != null && (
            <button className="ioc-detail-action-btn" onClick={handleCopy} title="Copy to clipboard">
              {COPY_ICON} Copy
            </button>
          )}
          {actions?.map((a) => {
            const inner = <>{a.iconPath || EXT_ICON} {a.label}</>;
            if (a.href) {
              return (
                <a key={a.key} href={a.href} target={a.external ? '_blank' : undefined} rel={a.external ? 'noreferrer' : undefined} className="ioc-detail-action-btn">
                  {inner}
                </a>
              );
            }
            if (a.to) {
              return (
                <a key={a.key} href={a.to} className="ioc-detail-action-btn">{inner}</a>
              );
            }
            return (
              <button key={a.key} className="ioc-detail-action-btn" onClick={a.onClick}>{inner}</button>
            );
          })}
        </div>
      </div>
      {category && <div className="ioc-detail-category-title">{category}</div>}
      <h1 className="ioc-detail-value">{value}</h1>
      {metaStrip && <div className="ioc-detail-meta-strip">{metaStrip}</div>}
      {belowValue}
    </div>
  );
}
